import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CheckCircle2, Save, Trash2 } from 'lucide-react';
import { EyeExaminationStatus, PERMISSIONS, type EyeExaminationDetail } from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { FormField } from '@/components/ui/FormField';
import { LoadingState } from '@/components/ui/LoadingState';
import { PageHeader } from '@/components/ui/PageHeader';
import { Select } from '@/components/ui/Select';
import { useBranch } from '@/hooks/useBranch';
import { useLocale } from '@/hooks/useLocale';
import { usePermissions } from '@/hooks/usePermissions';
import { useCustomerQuery } from '@/modules/customers/api/customers.api';
import { apiErrorCode } from '@/services/apiClient';
import { useAuthStore } from '@/store/authStore';
import {
  useAmendEyeExaminationMutation,
  useBranchExaminersQuery,
  useCompleteEyeExaminationMutation,
  useCreateEyeExaminationMutation,
  useCustomerExaminationsQuery,
  useDiscardEyeExaminationMutation,
  useExaminationExaminersQuery,
  useEyeExaminationQuery,
  useSaveEyeExaminationMutation,
  useVoidEyeExaminationMutation,
} from '../api/medical.api';
import { isolate } from '../bidi';
import { AcuityGrid } from '../components/AcuityGrid';
import { ClinicalEyeGrid } from '../components/ClinicalEyeGrid';
import { ClinicalTextArea } from '../components/ClinicalTextArea';
import { ExaminationStatusBadge } from '../components/ExaminationStatusBadge';
import { PrescriptionSection, VisitSection } from '../components/ExaminationSections';
import {
  AmendmentContext,
  CorrectionsSection,
  LifecycleSummary,
} from '../components/LifecycleSections';
import { ReasonDialog } from '../components/ReasonDialog';
import {
  loadErrorKey,
  readLifecycleRefusal,
  readWriteError,
  type LifecycleAction,
} from '../examinationErrors';
import {
  ACUITY_EXAMPLES,
  visibleRefractionKinds,
  copySubjectiveToPrescription,
  emptyExaminationForm,
  examinationPayload,
  eyeHasValues,
  fieldPath,
  formFromDetail,
  validateExaminationForm,
  type ExaminationForm,
  type FieldError,
  type FormErrors,
} from '../examinationForm';
import { useUnsavedChangesGuard } from '../useUnsavedChangesGuard';

/** Distinguishes one saved state of a record from the next, so a save replaces the form and a background read never does. */
const stateOf = (detail: EyeExaminationDetail) => `${detail.id}:${detail.version}:${detail.status}`;

/** A refused correction, with the open amendment to go to when that is what stood in the way. */
interface LifecycleProblem extends FieldError {
  amendmentId?: string;
}

/**
 * An eye examination: started, saved as a draft, completed by its examiner, then read-only for good.
 *
 * Saving is explicit and sends the whole draft with the version the screen was showing. A refused stale save is reported and never retried over; nothing clinical is kept anywhere but in memory until it is saved. A completed examination is corrected by an amendment or retired by a void, and a draft is discarded; each keeps the record, and a refused one is explained after reading the record again.
 */
export function EyeExaminationPage() {
  const { t } = useTranslation();
  const { isRtl } = useLocale();
  const navigate = useNavigate();
  const location = useLocation();
  const { customerId = '', examinationId } = useParams<{
    customerId: string;
    examinationId?: string;
  }>();
  const isNew = examinationId === undefined;
  const { can } = usePermissions();
  const userId = useAuthStore((state) => state.user?.id ?? null);
  const { activeBranch } = useBranch();

  const customer = useCustomerQuery(customerId);
  const examination = useEyeExaminationQuery(examinationId);
  const detail = examination.data;

  const isDraft = isNew || detail?.status === EyeExaminationStatus.DRAFT;
  const isCompleted = detail?.status === EyeExaminationStatus.COMPLETED;
  const canEdit =
    isDraft && (isNew ? can(PERMISSIONS.MEDICAL_CREATE) : can(PERMISSIONS.MEDICAL_UPDATE));
  const canComplete = can(PERMISSIONS.MEDICAL_COMPLETE);
  const canAmend = can(PERMISSIONS.MEDICAL_CREATE);
  const canVoid = can(PERMISSIONS.MEDICAL_VOID);

  const branchExaminers = useBranchExaminersQuery(activeBranch?.id ?? null, isNew && canEdit);
  const recordExaminers = useExaminationExaminersQuery(examinationId, !isNew && canEdit);
  const examiners = isNew ? branchExaminers.data : recordExaminers.data;

  // A completed record needs to know whether an amendment of it is already open; the history list says, identifiers only.
  const history = useCustomerExaminationsQuery(customerId, true, isCompleted);
  const openAmendment = isCompleted
    ? history.data?.items.find(
        (item) =>
          item.replacesExaminationId === detail?.id && item.status === EyeExaminationStatus.DRAFT,
      )
    : undefined;
  const original = useEyeExaminationQuery(detail?.replacesExaminationId ?? undefined);

  const [form, setForm] = useState<ExaminationForm | null>(null);
  const [baseline, setBaseline] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});
  const [banner, setBanner] = useState<FieldError | null>(null);
  const [notice, setNotice] = useState<FieldError | null>(null);
  const [problem, setProblem] = useState<LifecycleProblem | null>(null);
  const [conflict, setConflict] = useState(false);
  const [confirmingComplete, setConfirmingComplete] = useState(false);
  const [confirmingReload, setConfirmingReload] = useState(false);
  const [confirmingCopy, setConfirmingCopy] = useState(false);
  const [lifecycleDialog, setLifecycleDialog] = useState<LifecycleAction | null>(null);
  const loaded = useRef<string | null>(null);

  const serialised = useMemo(() => (form ? JSON.stringify(form) : ''), [form]);
  const isDirty = form !== null && serialised !== baseline;
  const guard = useUnsavedChangesGuard(canEdit && isDirty);

  const create = useCreateEyeExaminationMutation(customerId);
  const save = useSaveEyeExaminationMutation(examinationId ?? '');
  const complete = useCompleteEyeExaminationMutation(examinationId ?? '');
  const amend = useAmendEyeExaminationMutation(examinationId ?? '');
  const voidExamination = useVoidEyeExaminationMutation(examinationId ?? '');
  const discard = useDiscardEyeExaminationMutation(examinationId ?? '');

  const adopt = (next: ExaminationForm) => {
    setForm(next);
    setBaseline(JSON.stringify(next));
  };

  // A new examination starts empty, dated now.
  useEffect(() => {
    if (isNew && form === null) adopt(emptyExaminationForm(new Date(), ''));
  }, [isNew, form]);

  // The person starting it is named examiner when they may be one — only while nothing has been touched.
  useEffect(() => {
    if (!isNew || !form || !userId || !examiners || form.examinerId !== '' || isDirty) return;
    if (examiners.some((examiner) => examiner.id === userId))
      adopt({ ...form, examinerId: userId });
  }, [isNew, form, userId, examiners, isDirty]);

  // A saved record fills the form. A later read never overwrites unsaved edits; it is reported as a conflict instead.
  useEffect(() => {
    if (!detail) return;
    const state = stateOf(detail);
    if (loaded.current === state) return;
    if (loaded.current !== null && isDirty) {
      setConflict(true);
      return;
    }
    loaded.current = state;
    adopt(formFromDetail(detail));
  }, [detail, isDirty]);

  // Arriving on a record straight after its first save, or on an amendment just started.
  const { resetAllowance } = guard;
  const arrival = location.state as { saved?: boolean; amendmentOf?: string } | null;
  const arrivedSaved = arrival?.saved === true;
  const arrivedAmendmentOf = arrival?.amendmentOf ?? null;
  useEffect(() => {
    resetAllowance();
    if (arrivedSaved) setNotice({ key: 'medical.page.saved' });
    if (arrivedAmendmentOf) {
      setNotice({
        key: 'medical.lifecycle.amendStarted',
        values: { number: isolate(arrivedAmendmentOf) },
      });
    }
  }, [examinationId, arrivedSaved, arrivedAmendmentOf, resetAllowance]);

  if (customer.isPending || (!isNew && examination.isPending) || form === null) {
    if (!isNew && examination.isError) {
      return (
        <PageShell customerId={customerId} isRtl={isRtl}>
          <Alert tone="error">{t(loadErrorKey(examination.error))}</Alert>
        </PageShell>
      );
    }
    return <LoadingState />;
  }

  const update = canEdit
    ? (next: ExaminationForm) => {
        setNotice(null);
        setForm(next);
      }
    : undefined;

  const handleOutcome = (error: unknown, payload: ReturnType<typeof examinationPayload>) => {
    const outcome = readWriteError(error, payload);
    if (outcome.kind === 'conflict') {
      setConflict(true);
      return;
    }
    setErrors(outcome.fields);
    setBanner(outcome.banner);
  };

  const handleSave = () => {
    setNotice(null);
    setBanner(null);
    setProblem(null);
    const found = validateExaminationForm(form);
    setErrors(found);
    if (Object.keys(found).length > 0) {
      setBanner({ key: 'medical.errors.validation' });
      return;
    }

    const payload = examinationPayload(
      form,
      detail ? { examinedAt: detail.examinedAt } : undefined,
    );
    if (isNew) {
      create.mutate(payload, {
        onSuccess: (created) => {
          loaded.current = stateOf(created);
          adopt(formFromDetail(created));
          guard.allowNextNavigation();
          void navigate(`/customers/${customerId}/eye-examinations/${created.id}`, {
            replace: true,
            state: { saved: true },
          });
        },
        onError: (error) => handleOutcome(error, payload),
      });
      return;
    }
    if (!detail) return;
    save.mutate(
      { ...payload, version: detail.version },
      {
        onSuccess: (saved) => {
          loaded.current = stateOf(saved);
          adopt(formFromDetail(saved));
          setConflict(false);
          setNotice({ key: 'medical.page.saved' });
        },
        onError: (error) => handleOutcome(error, payload),
      },
    );
  };

  const handleComplete = () => {
    if (!detail) return;
    setBanner(null);
    setProblem(null);
    complete.mutate(
      { version: detail.version },
      {
        onSuccess: (completed) => {
          loaded.current = stateOf(completed);
          adopt(formFromDetail(completed));
          setConfirmingComplete(false);
          setErrors({});
          setNotice({ key: 'medical.page.completed' });
        },
        onError: (error) => {
          setConfirmingComplete(false);
          // An amendment is also refused when the record it corrects is no longer completed; reading that record tells the two apart.
          if (apiErrorCode(error) === 'CONFLICT' && detail.replacesExaminationId) {
            void original.refetch().then(({ data }) => {
              if (data && data.status !== EyeExaminationStatus.COMPLETED) {
                setProblem({
                  key: 'medical.conflict.originalNotCompleted',
                  values: { number: isolate(data.examinationNumber) },
                });
              } else {
                setConflict(true);
              }
            });
            return;
          }
          handleOutcome(error, examinationPayload(form, { examinedAt: detail.examinedAt }));
        },
      },
    );
  };

  /** The record a void or discard answered with replaces the screen, like a save. */
  const settleLifecycle = (next: EyeExaminationDetail, noticeKey: string) => {
    setLifecycleDialog(null);
    loaded.current = stateOf(next);
    adopt(formFromDetail(next));
    setErrors({});
    setBanner(null);
    setConflict(false);
    setProblem(null);
    setNotice({ key: noticeKey });
  };

  /** A 409 means an amendment is open, or the record changed: reading both again says which, and nothing is retried. */
  const handleLifecycleRefusal = async (action: LifecycleAction, error: unknown) => {
    setLifecycleDialog(null);
    if (apiErrorCode(error) !== 'CONFLICT') {
      setProblem(readLifecycleRefusal(action, error));
      return;
    }
    if (action === 'discard') {
      setConflict(true);
      return;
    }

    const [fresh, list] = await Promise.all([examination.refetch(), history.refetch()]);
    const open = list.data?.items.find(
      (item) =>
        item.replacesExaminationId === examinationId && item.status === EyeExaminationStatus.DRAFT,
    );
    if (fresh.data?.status !== EyeExaminationStatus.COMPLETED) {
      setProblem({ key: 'medical.conflict.changed' });
    } else if (action === 'amend') {
      setProblem({ key: 'medical.conflict.amendmentOpen', amendmentId: open?.id });
    } else if (open) {
      setProblem({
        key: 'medical.lifecycle.voidBlocked',
        values: { number: isolate(open.examinationNumber) },
        amendmentId: open.id,
      });
    } else {
      setProblem({ key: 'medical.lifecycle.voidBlockedUnknown' });
    }
  };

  const handleAmend = (reason: string) => {
    if (!detail) return;
    setProblem(null);
    amend.mutate(
      { reason },
      {
        onSuccess: (created) => {
          setLifecycleDialog(null);
          void navigate(`/customers/${customerId}/eye-examinations/${created.id}`, {
            state: { amendmentOf: detail.examinationNumber },
          });
        },
        onError: (error) => void handleLifecycleRefusal('amend', error),
      },
    );
  };

  const handleVoid = (reason: string) => {
    setProblem(null);
    voidExamination.mutate(
      { reason },
      {
        onSuccess: (voided) => settleLifecycle(voided, 'medical.lifecycle.voided'),
        onError: (error) => void handleLifecycleRefusal('void', error),
      },
    );
  };

  const handleDiscard = (reason: string) => {
    if (!detail) return;
    setProblem(null);
    discard.mutate(
      { reason, version: detail.version },
      {
        onSuccess: (discarded) => settleLifecycle(discarded, 'medical.lifecycle.discarded'),
        onError: (error) => void handleLifecycleRefusal('discard', error),
      },
    );
  };

  const reloadLatest = () => {
    setConfirmingReload(false);
    void examination.refetch().then(({ data }) => {
      if (!data) return;
      loaded.current = stateOf(data);
      adopt(formFromDetail(data));
      setConflict(false);
      setErrors({});
      setBanner(null);
    });
  };

  const copyFromSubjective = () => {
    setConfirmingCopy(false);
    update?.(copySubjectiveToPrescription(form));
  };
  const requestCopy = () => {
    const current = form.prescription;
    if (eyeHasValues(current.right) || eyeHasValues(current.left)) setConfirmingCopy(true);
    else copyFromSubjective();
  };

  const examinerName =
    detail?.examinerName ??
    examiners?.find((examiner) => examiner.id === form.examinerId)?.name ??
    null;
  const completionBlocker: FieldError | null = isNew
    ? { key: 'medical.review.saveNewFirst' }
    : !canComplete
      ? { key: 'medical.review.noCompletePermission' }
      : isDirty
        ? { key: 'medical.review.saveFirst' }
        : !detail?.examinerId
          ? { key: 'medical.review.examinerRequired' }
          : detail.examinerId !== userId
            ? { key: 'medical.review.onlyExaminer', values: { name: detail.examinerName ?? '—' } }
            : null;

  const title = isNew
    ? t('medical.page.newTitle')
    : t('medical.page.title', { number: isolate(detail?.examinationNumber ?? '') });
  const customerLabel = customer.data
    ? `${customer.data.displayName} · ${isolate(customer.data.customerNumber)}`
    : '—';
  const saving = create.isPending || save.isPending;
  const number = isolate(detail?.examinationNumber ?? '');
  const originalNumber = isolate(detail?.replacesExaminationNumber ?? '—');

  return (
    <PageShell customerId={customerId} isRtl={isRtl}>
      <PageHeader
        title={title}
        subtitle={customer.data?.displayName}
        actions={detail ? <ExaminationStatusBadge status={detail.displayStatus} /> : null}
      />

      {banner ? <Alert tone="error">{t(banner.key, banner.values)}</Alert> : null}
      {conflict ? (
        <Alert tone="warning">
          <span className="block">{t('medical.conflict.message')}</span>
          <Button
            size="sm"
            variant="secondary"
            className="mt-2"
            onClick={() => setConfirmingReload(true)}
          >
            {t('medical.conflict.reload')}
          </Button>
        </Alert>
      ) : null}
      {problem ? (
        <Alert tone="warning">
          <span className="block">{t(problem.key, problem.values)}</span>
          {problem.amendmentId ? (
            <Link
              to={`/customers/${customerId}/eye-examinations/${problem.amendmentId}`}
              className="text-brand-700 hover:text-brand-800 mt-1 inline-block font-medium underline underline-offset-2"
            >
              {t('medical.lifecycle.openExistingAmendment')}
            </Link>
          ) : null}
        </Alert>
      ) : null}
      {notice ? <Alert tone="success">{t(notice.key, notice.values)}</Alert> : null}
      {isNew && !activeBranch ? <Alert tone="warning">{t('medical.page.noBranch')}</Alert> : null}
      {detail ? (
        <AmendmentContext detail={detail} customerId={customerId} original={original.data} />
      ) : null}
      {detail && !isDraft ? <LifecycleSummary detail={detail} customerId={customerId} /> : null}

      <Section title={t('medical.sections.visit')}>
        <VisitSection
          form={form}
          update={update}
          customerLabel={customerLabel}
          branchName={isNew ? (activeBranch?.name ?? null) : (detail?.branchName ?? null)}
          examiners={examiners}
          examinerName={examinerName}
          examinedAtIso={detail?.examinedAt ?? null}
          errors={errors}
        />
      </Section>

      <Section title={t('medical.sections.acuity')}>
        {update ? (
          <div className="mb-4 grid gap-4 md:grid-cols-2">
            <FormField label={t('medical.acuity.distanceNotation')}>
              {({ id }) => (
                <Select
                  id={id}
                  value={form.distanceAcuityNotation}
                  options={(['SNELLEN_METRIC', 'DECIMAL'] as const).map((notation) => ({
                    value: notation,
                    label: t(`medical.acuity.notations.${notation}`),
                  }))}
                  onChange={(event) =>
                    update({
                      ...form,
                      distanceAcuityNotation: event.target
                        .value as ExaminationForm['distanceAcuityNotation'],
                    })
                  }
                />
              )}
            </FormField>
            <FormField label={t('medical.acuity.nearNotation')}>
              {({ id }) => (
                <Select
                  id={id}
                  value={form.nearAcuityNotation}
                  options={(['N_POINT', 'DECIMAL'] as const).map((notation) => ({
                    value: notation,
                    label: t(`medical.acuity.notations.${notation}`),
                  }))}
                  onChange={(event) =>
                    update({
                      ...form,
                      nearAcuityNotation: event.target
                        .value as ExaminationForm['nearAcuityNotation'],
                    })
                  }
                />
              )}
            </FormField>
          </div>
        ) : (
          <p className="text-muted mb-3 text-sm">
            {t('medical.acuity.distanceNotation')}:{' '}
            {t(`medical.acuity.notations.${form.distanceAcuityNotation}`)} ·{' '}
            {t('medical.acuity.nearNotation')}:{' '}
            {t(`medical.acuity.notations.${form.nearAcuityNotation}`)}
          </p>
        )}
        <AcuityGrid
          value={form.acuities}
          onChange={update ? (acuities) => update({ ...form, acuities }) : undefined}
          errors={errors}
        />
        {update ? (
          <p className="text-muted mt-2 text-xs">
            {t('medical.acuity.hint', {
              distance: ACUITY_EXAMPLES[form.distanceAcuityNotation],
              near: ACUITY_EXAMPLES[form.nearAcuityNotation],
            })}
          </p>
        ) : null}
      </Section>

      <Section title={t('medical.sections.refraction')}>
        <div className="space-y-6">
          {visibleRefractionKinds(detail?.refractions ?? []).map((kind) => {
            const pair = form.refractions[kind];
            const empty = !eyeHasValues(pair.right) && !eyeHasValues(pair.left);
            return (
              <div key={kind}>
                <h3 className="text-ink mb-2 text-sm font-semibold">
                  {t(`medical.refraction.kinds.${kind}`)}
                </h3>
                {!update && empty ? (
                  <p className="text-muted text-sm">{t('medical.refraction.empty')}</p>
                ) : (
                  <ClinicalEyeGrid
                    caption={t(`medical.refraction.kinds.${kind}`)}
                    value={pair}
                    onChange={
                      update
                        ? (next) =>
                            update({ ...form, refractions: { ...form.refractions, [kind]: next } })
                        : undefined
                    }
                    errors={errors}
                    errorPath={(eye, field) => fieldPath.refraction(kind, eye, field)}
                  />
                )}
              </div>
            );
          })}
          {update ? <p className="text-muted text-xs">{t('medical.refraction.hint')}</p> : null}
        </div>
      </Section>

      <Section title={t('medical.sections.prescription')}>
        <PrescriptionSection
          customerId={customerId}
          form={form}
          update={update}
          onCopyFromSubjective={requestCopy}
          detail={detail}
          canViewMeasurements={can(PERMISSIONS.MEASUREMENTS_VIEW)}
          errors={errors}
          sourcePd={
            isDraft && detail?.replacesExaminationId
              ? (original.data?.prescription?.pd ?? null)
              : null
          }
        />
      </Section>

      <Section title={t('medical.sections.notes')}>
        <ClinicalTextArea
          label={t('medical.notes.clinicalNotes')}
          value={form.clinicalNotes}
          maxLength={2000}
          rows={4}
          onChange={update ? (clinicalNotes) => update({ ...form, clinicalNotes }) : undefined}
        />
      </Section>

      {isDraft ? (
        <Section title={t('medical.sections.review')}>
          <p className="text-ink text-sm" role="status">
            {completionBlocker
              ? t(completionBlocker.key, completionBlocker.values)
              : t('medical.review.ready')}
          </p>
        </Section>
      ) : null}

      {isCompleted && (canAmend || canVoid || openAmendment) ? (
        <Section title={t('medical.lifecycle.section')}>
          <CorrectionsSection
            customerId={customerId}
            canAmend={canAmend}
            canVoid={canVoid}
            openAmendment={openAmendment}
            onAmend={() => setLifecycleDialog('amend')}
            onVoid={() => setLifecycleDialog('void')}
          />
        </Section>
      ) : null}

      {isDraft && (canEdit || canComplete) ? (
        <div className="border-line sticky bottom-0 z-10 -mx-4 flex flex-wrap items-center justify-end gap-2 border-t bg-white/95 px-4 py-3 backdrop-blur">
          {!isNew && canEdit ? (
            <Button
              variant="ghost"
              className="me-auto"
              onClick={() => setLifecycleDialog('discard')}
              disabled={saving}
            >
              <Trash2 className="size-4" aria-hidden />
              {t('medical.lifecycle.discard')}
            </Button>
          ) : null}
          {canEdit ? (
            <Button
              variant="secondary"
              onClick={handleSave}
              isLoading={saving}
              disabled={saving || (isNew && !activeBranch)}
            >
              <Save className="size-4" aria-hidden />
              {t('medical.page.saveDraft')}
            </Button>
          ) : null}
          {canComplete ? (
            <Button
              onClick={() => setConfirmingComplete(true)}
              disabled={completionBlocker !== null || saving}
            >
              <CheckCircle2 className="size-4" aria-hidden />
              {t('medical.page.complete')}
            </Button>
          ) : null}
        </div>
      ) : null}

      {/* Each dialog is mounted only while it is asked, so the page never holds two dialogs whose titles share an id. */}
      {confirmingComplete ? (
        <ConfirmDialog
          isOpen
          onCancel={() => setConfirmingComplete(false)}
          onConfirm={handleComplete}
          isConfirming={complete.isPending}
          title={t('medical.review.confirmTitle')}
          message={
            detail?.replacesExaminationId
              ? t('medical.review.confirmAmendmentMessage', { number: originalNumber })
              : t('medical.review.confirmMessage')
          }
          confirmLabel={t('medical.review.confirm')}
        />
      ) : null}
      {confirmingReload ? (
        <ConfirmDialog
          isOpen
          onCancel={() => setConfirmingReload(false)}
          onConfirm={reloadLatest}
          isDestructive
          title={t('medical.conflict.reloadTitle')}
          message={t('medical.conflict.reloadMessage')}
          confirmLabel={t('medical.conflict.reload')}
        />
      ) : null}
      {confirmingCopy ? (
        <ConfirmDialog
          isOpen
          onCancel={() => setConfirmingCopy(false)}
          onConfirm={copyFromSubjective}
          title={t('medical.prescription.copyConfirmTitle')}
          message={t('medical.prescription.copyConfirmMessage')}
          confirmLabel={t('medical.prescription.copyConfirm')}
        />
      ) : null}
      {lifecycleDialog === 'amend' ? (
        <ReasonDialog
          title={t('medical.lifecycle.amendTitle')}
          points={[
            t('medical.lifecycle.amendNotEdited', { number }),
            t('medical.lifecycle.amendNewDraft'),
            t('medical.lifecycle.amendKept', { number }),
            t('medical.lifecycle.amendReplacedLater', { number }),
          ]}
          reasonLabel={t('medical.lifecycle.amendReason')}
          confirmLabel={t('medical.lifecycle.amendConfirm')}
          isSubmitting={amend.isPending}
          onCancel={() => setLifecycleDialog(null)}
          onConfirm={handleAmend}
        />
      ) : null}
      {lifecycleDialog === 'void' ? (
        <ReasonDialog
          title={t('medical.lifecycle.voidTitle')}
          points={[
            t('medical.lifecycle.voidKept'),
            t('medical.lifecycle.voidNotActive'),
            t('medical.lifecycle.voidNothingDeleted'),
          ]}
          reasonLabel={t('medical.lifecycle.voidReason')}
          confirmLabel={t('medical.lifecycle.voidConfirm')}
          isDestructive
          isSubmitting={voidExamination.isPending}
          onCancel={() => setLifecycleDialog(null)}
          onConfirm={handleVoid}
        />
      ) : null}
      {lifecycleDialog === 'discard' ? (
        <ReasonDialog
          title={t('medical.lifecycle.discardTitle')}
          points={[
            t('medical.lifecycle.discardNotDeleted'),
            t('medical.lifecycle.discardFinal'),
            ...(detail?.replacesExaminationId
              ? [t('medical.lifecycle.discardAmendment', { number: originalNumber })]
              : []),
          ]}
          reasonLabel={t('medical.lifecycle.discardReason')}
          confirmLabel={t('medical.lifecycle.discardConfirm')}
          isDestructive
          isSubmitting={discard.isPending}
          onCancel={() => setLifecycleDialog(null)}
          onConfirm={handleDiscard}
        />
      ) : null}
      {guard.isBlocked ? (
        <ConfirmDialog
          isOpen
          onCancel={guard.stay}
          onConfirm={guard.leave}
          isDestructive
          title={t('medical.leave.title')}
          message={t('medical.leave.message')}
          confirmLabel={t('medical.leave.confirm')}
        />
      ) : null}
    </PageShell>
  );
}

function PageShell({
  customerId,
  isRtl,
  children,
}: {
  customerId: string;
  isRtl: boolean;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const BackIcon = isRtl ? ArrowRight : ArrowLeft;
  return (
    <div className="space-y-6">
      <Link
        to={`/customers/${customerId}`}
        className="text-muted hover:text-ink inline-flex items-center gap-1.5 text-sm"
      >
        <BackIcon className="size-4" aria-hidden />
        {t('medical.page.backToCustomer')}
      </Link>
      {children}
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Card>
      <CardHeader title={title} />
      <CardBody>{children}</CardBody>
    </Card>
  );
}
